import { createContext, ReactNode, useContext } from "react";
import { useFlags } from "@featbit/react-client-sdk";
import { flagDefaults } from "src/consts/flagDefaults";
import { FlagValue, Flags } from "src/consts/flags";

const FlagOverridesContext = createContext<Partial<Record<Flags, FlagValue>>>({});

export function FlagOverridesProvider({
    children,
    overrides,
}: {
    children: ReactNode;
    overrides: Partial<Record<Flags, FlagValue>>;
}) {
    return <FlagOverridesContext.Provider value={overrides}>{children}</FlagOverridesContext.Provider>;
}

function useFlag(flag: Flags, type: "boolean" | "number" | "object" = "boolean"): boolean | number | object {
    const featbitFlags = useFlags() as Partial<Record<Flags, FlagValue>>;
    const flagOverrides = useContext(FlagOverridesContext);
    const value = flagOverrides[flag] ?? featbitFlags[flag] ?? flagDefaults[flag];

    switch (type) {
        case "boolean":
            return Boolean(value);
        case "number":
            return Number(value);
        case "object":
        default:
            return value;
    }
}

export function useBooleanFlag(flag: Flags): boolean {
    return useFlag(flag, "boolean") as boolean;
}

export function useNumberFlag(flag: Flags): number {
    return useFlag(flag, "number") as number;
}

export function useObjectFlag<T extends Record<string, unknown>>(flag: Flags): T {
    return useFlag(flag, "object") as T;
}
